import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import AvailabilityService from '@/services/AvailabilityService'
import ProjectsService from '@/services/ProjectsService'
import TablicaService from '@/services/TablicaService'
import { WOJEWODZTWA } from '@/pages/system/Uzytkownicy/utils/wojewodztwa'
import {
	buildMonthDays,
	cellKey,
	toDayKey,
	voivodeshipOrder,
} from '../boardCells'

const trainerIdOf = user => `${user?._id || user?.id || ''}`

const relationId = value => {
	if (!value) return ''
	if (typeof value === 'object') return `${value._id || value.id || ''}`
	return `${value}`
}

const eachDay = (startValue, endValue) => {
	const start = moment(toDayKey(startValue), 'YYYY-MM-DD', true)
	const end = moment(toDayKey(endValue || startValue), 'YYYY-MM-DD', true)
	if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return []
	const days = []
	const cursor = start.clone()
	while (!cursor.isAfter(end, 'day')) {
		days.push(cursor.format('YYYY-MM-DD'))
		cursor.add(1, 'day')
		if (days.length > 366) break
	}
	return days
}

export default function useWirtualnaTablica() {
	const now = moment()
	const [year, setYear] = useState(now.year())
	const [month, setMonth] = useState(now.month())
	const [voivodeship, setVoivodeship] = useState('')
	const [trainers, setTrainers] = useState([])
	const [availabilityByCell, setAvailabilityByCell] = useState({})
	const [projectsByCell, setProjectsByCell] = useState({})
	const [colors, setColors] = useState({})
	const [selectedCell, setSelectedCell] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [colorError, setColorError] = useState('')

	const days = useMemo(() => buildMonthDays(year, month), [month, year])
	const range = useMemo(() => ({
		start: days[0]?.key || '',
		end: days[days.length - 1]?.key || '',
	}), [days])

	const load = useCallback(async () => {
		if (!range.start || !range.end) return
		setLoading(true)
		setError('')

		const [trainersResult, availabilityResult, projectsResult, colorsResult] = await Promise.all([
			TablicaService.getTrainers(),
			AvailabilityService.getWithFilters({
				limit: 2000,
				sortColumn: 'startDate',
				sortDirection: 'asc',
				filters: { startDate: range.start, endDate: range.end },
			}),
			ProjectsService.getAll({ limit: 500, sortColumn: 'created', sortDirection: 'desc' }),
			TablicaService.getColors({ startDate: range.start, endDate: range.end }),
		])

		const nextError = trainersResult.error || availabilityResult.error || projectsResult.error || colorsResult.error
		setError(nextError || '')

		const nextAvailability = {}
		;(availabilityResult.data || []).forEach(item => {
			const trainerId = relationId(item.userId || item.trainerId)
			const day = toDayKey(item.startDate || item.dateFrom)
			if (!trainerId || !day) return
			const morning = item.morning === true || (Array.isArray(item.slots) && item.slots.includes('8-14'))
			const afternoon = item.afternoon === true || (Array.isArray(item.slots) && item.slots.includes('14-20'))
			nextAvailability[cellKey(trainerId, day)] = { morning, afternoon }
		})

		const nextProjects = {}
		;(projectsResult.data || []).forEach(project => {
			const projectId = `${project?._id || project?.id || ''}`
			const projectName = `${project?.nazwa || ''}`.trim()
			;(project.steps || []).forEach(step => {
				const stepDays = eachDay(step.data_rozpoczecia, step.data_zakonczenia)
				const trainerIds = (step.pozycje || [])
					.map(item => relationId(item.trainerId))
					.filter(Boolean)
				stepDays.forEach(day => {
					if (day < range.start || day > range.end) return
					trainerIds.forEach(trainerId => {
						const key = cellKey(trainerId, day)
						if (!nextProjects[key]) nextProjects[key] = []
						if (!nextProjects[key].some(entry => entry.id === projectId)) {
							nextProjects[key].push({
								id: projectId,
								name: projectName || step.nazwa || 'Projekt',
							})
						}
					})
				})
			})
		})

		const nextColors = {}
		;(colorsResult.data || []).forEach(item => {
			const trainerId = relationId(item.userId)
			const day = toDayKey(item.date)
			if (!trainerId || !day || !item.color) return
			nextColors[cellKey(trainerId, day)] = item.color
		})

		setTrainers(trainersResult.data || [])
		setAvailabilityByCell(nextAvailability)
		setProjectsByCell(nextProjects)
		setColors(nextColors)
		setSelectedCell(null)
		setLoading(false)
	}, [range.end, range.start])

	useEffect(() => {
		load()
	}, [load])

	const visibleTrainers = useMemo(() => {
		return (trainers || [])
			.map(trainer => ({
				id: trainerIdOf(trainer),
				name: `${trainer.nazwa || trainer.name || ''}`.trim() || '—',
				voivodeship: `${trainer.wojewodztwo || ''}`.trim(),
			}))
			.filter(trainer => trainer.id)
			.filter(trainer => !voivodeship || trainer.voivodeship === voivodeship)
			.sort((left, right) => {
				const byRegion = voivodeshipOrder(left.voivodeship) - voivodeshipOrder(right.voivodeship)
				if (byRegion !== 0) return byRegion
				return left.name.localeCompare(right.name, 'pl')
			})
	}, [trainers, voivodeship])

	const handleSelectCell = useCallback((trainerId, day) => {
		setColorError('')
		setSelectedCell(prev => (
			prev?.trainerId === trainerId && prev?.day === day ? null : { trainerId, day }
		))
	}, [])

	const handleSetColor = useCallback(async color => {
		if (!selectedCell) return
		const key = cellKey(selectedCell.trainerId, selectedCell.day)
		const previous = colors[key] || ''
		setColors(prev => {
			const next = { ...prev }
			if (color) next[key] = color
			else delete next[key]
			return next
		})
		setColorError('')
		const result = await TablicaService.saveColor({
			userId: selectedCell.trainerId,
			date: selectedCell.day,
			color,
		})
		if (!result.success) {
			setColors(prev => ({ ...prev, [key]: previous }))
			setColorError(result.error || '')
		}
	}, [colors, selectedCell])

	return {
		colorError,
		colors,
		days,
		error,
		handleSelectCell,
		handleSetColor,
		loading,
		month,
		projectsByCell,
		availabilityByCell,
		selectedCell,
		setMonth,
		setVoivodeship,
		setYear,
		visibleTrainers,
		voivodeship,
		voivodeships: WOJEWODZTWA,
		year,
	}
}
